import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/ui/lib/utils';

// Card は面（背景・枠線・角丸・余白リズム）だけを持つ抽象コンポーネント。
// 中身（カバー画像・ロゴ・タグ・進捗バー等）はページ側の合成で表現する。
// 余白は --card-spacing で一括制御し、利用側の className で上書きできる。
// 左右だけ別に詰めたい面のために --card-spacing-inline を分けている
// （既定は --card-spacing と同じ）。
const cardVariants = cva(
  'tw:box-border tw:flex tw:flex-col tw:gap-(--card-spacing) tw:overflow-hidden tw:rounded-md tw:bg-card tw:py-(--card-spacing) tw:text-sm tw:text-card-foreground tw:[--card-spacing:--spacing(5)] tw:[--card-spacing-inline:var(--card-spacing)] tw:has-[>[data-slot=card-footer]]:pb-0 tw:has-[>img:first-child]:pt-0',
  {
    variants: {
      // elevated は案件カード（募集ページ）の影あり表現。
      // borderless は枠線を持たない面（画像が縁まで届くカード）。
      // 枠線は base ではなく variant 側に持たせている。cn() の twMerge は
      // tw: プレフィックスを解決しないため、base の tw:border を
      // tw:border-0 で打ち消すと両方残り、順序依存になってしまう。
      variant: {
        default: 'tw:border tw:border-border',
        elevated: 'tw:border tw:border-border tw:shadow-elevated',
        // 画像が縁まで届く面なので、本文の左右も縁に揃える
        borderless: 'tw:[--card-spacing-inline:0px]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type CardProps = React.ComponentProps<'div'> &
  VariantProps<typeof cardVariants>;

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card"
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-header"
    className={cn(
      'tw:flex tw:flex-col tw:gap-1 tw:px-(--card-spacing-inline)',
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

// 見出しとして扱いたいページのために要素だけ差し替えられるようにする。
// 既定は div のままなので、既存の利用箇所の描画は変わらない。
// Preflight を読み込んでいないため、h1-h6 の UA 既定マージンは打ち消す
// （文字サイズと太さは元から text-base / font-bold で上書きしている）。
type CardTitleProps = React.ComponentProps<'div'> & {
  as?: 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

const CardTitle = React.forwardRef<HTMLDivElement, CardTitleProps>(
  ({ className, as: Component = 'div', ...props }, ref) => (
    <Component
      ref={ref}
      data-slot="card-title"
      className={cn(
        'tw:m-0 tw:text-base tw:leading-snug tw:font-bold',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-description"
    className={cn('tw:text-sm tw:text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-content"
    className={cn('tw:px-(--card-spacing-inline)', className)}
    {...props}
  />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card-footer"
    className={cn(
      'tw:flex tw:items-center tw:border-t tw:border-border tw:p-(--card-spacing)',
      className,
    )}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  cardVariants,
};
